/**
 * **The record page** (C14 §10.6 item 3, ADR-0033 §1; interface I-1).
 *
 *     <RecordPage
 *       back={{ to: '/imports', label: 'All imports' }}
 *       title={job.filename}
 *       status={{ kind: 'import', status: job.status }}
 *       facts={[{ label: 'Kind', value: 'Clients' }, { label: 'Rows', value: 120 }]}
 *       primary={<Button>Import 118 rows</Button>}
 *       more={[{ id: 'rejects', label: 'Download the refused rows', onSelect }]}
 *       tabs={[{ id: 'rejected', label: 'Refused', badge: 2, content: <…/> }]}
 *     >
 *       {alerts — anything above the parts}
 *     </RecordPage>
 *
 * One header: a back link, the title, its status badge, a line of key facts,
 * **one** primary action and everything else behind *More*. The record's
 * parts are `tabs` (the kit's `Tabs`), or `children` drawn as section cards
 * (`RecordSection`); `children` given with `tabs` sit above the tab strip.
 */

import {
  Alert,
  DropdownMenu,
  type DropdownMenuItem,
  PageHeader,
  Skeleton,
  TabPanel,
  Tabs,
  buttonClass,
} from '@fiveox/ui'
import { ChevronDown } from 'lucide-react'
import { Fragment, type ReactNode, useState } from 'react'

import { BackLink, type PageBack } from './ListPage'
import { StatusBadge, type StatusBadgeProps } from './StatusBadge'

export interface RecordFact {
  label: string
  value: ReactNode
}

export interface RecordTab {
  id: string
  label: ReactNode
  /** A count beside the label. */
  badge?: ReactNode
  content: ReactNode
}

export interface RecordPageProps {
  back: PageBack
  title: ReactNode
  status?: StatusBadgeProps
  /** The few facts a reader needs before anything else — *Client · Due · Total*. */
  facts?: RecordFact[]
  /** The one primary action (a `Button`). */
  primary?: ReactNode
  /** Every other action. A disabled item says why in its `description`. */
  more?: DropdownMenuItem[]
  tabs?: RecordTab[]
  /** Controlled tab; omit to let the page hold it (first tab to start). */
  tab?: string
  onTabChange?: (id: string) => void
  /** While the record has not arrived: the header's shape, nothing invented. */
  loading?: boolean
  error?: string | null
  children?: ReactNode
}

function Facts({ facts }: { facts: RecordFact[] }) {
  return (
    <dl className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" data-record-facts="">
      {facts.map((fact, index) => (
        <Fragment key={fact.label}>
          {index > 0 && (
            <span aria-hidden className="text-muted-foreground">
              ·
            </span>
          )}
          <div className="flex items-baseline gap-1.5">
            <dt className="text-muted-foreground">{fact.label}</dt>
            <dd className="text-foreground font-medium">{fact.value}</dd>
          </div>
        </Fragment>
      ))}
    </dl>
  )
}

export function RecordPage({
  back,
  title,
  status,
  facts,
  primary,
  more,
  tabs,
  tab,
  onTabChange,
  loading = false,
  error,
  children,
}: RecordPageProps) {
  const [ownTab, setOwnTab] = useState<string | null>(null)
  const ids = tabs?.map((t) => t.id) ?? []
  const wanted = tab ?? ownTab
  const active = wanted && ids.includes(wanted) ? wanted : (ids[0] ?? '')
  const choose = (id: string) => {
    setOwnTab(id)
    onTabChange?.(id)
  }

  const moreItems = more ?? []
  const actions =
    primary || moreItems.length > 0 ? (
      <>
        {moreItems.length > 0 && (
          <DropdownMenu
            label="More actions"
            items={moreItems}
            // A plain button in the kit's outline look: the trigger must take
            // the ref focus returns to, which the kit's `Button` does not type.
            trigger={({ ref, open: _open, ...props }) => (
              <button ref={ref} type="button" className={buttonClass({ variant: 'outline' })} {...props}>
                More <ChevronDown size={14} aria-hidden />
              </button>
            )}
          />
        )}
        {primary}
      </>
    ) : undefined

  if (loading) {
    return (
      <div data-record-page="" className="w-full min-w-0 space-y-6">
        <PageHeader
          back={<BackLink back={back} />}
          title={<Skeleton className="h-8 w-64 max-w-full" />}
          description={
            <div role="status" aria-busy aria-label="Loading">
              <Skeleton className="mt-1 h-4 w-80 max-w-full" />
            </div>
          }
        />
        {error && <Alert tone="error">{error}</Alert>}
        {!error && <Skeleton className="h-48 w-full rounded-2xl" />}
      </div>
    )
  }

  return (
    <div data-record-page="" className="w-full min-w-0 space-y-6">
      {/* The kit's `PageHeader` in shape, drawn here because its title block
          takes the width its facts ask for: a case's line of five facts
          pushed *More* and the primary under it. Here the title block gives
          way (`min-w-0 flex-1`, the facts wrapping inside it) and the actions
          keep the top right from `sm` up; on a phone they stack below. */}
      <div data-record-header="">
        <div className="mb-1 text-sm">
          <BackLink back={back} />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-foreground text-2xl font-semibold break-words">{title}</h1>
            {(status || (facts && facts.length > 0)) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {status && <StatusBadge size="md" {...status} />}
                {facts && facts.length > 0 && <Facts facts={facts} />}
              </div>
            )}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>}
        </div>
      </div>

      {error && <Alert tone="error">{error}</Alert>}

      {children}

      {tabs && tabs.length > 0 && (
        <div className="min-w-0 space-y-4">
          <Tabs
            idBase="record"
            active={active}
            onChange={choose}
            tabs={tabs.map(({ id, label, badge }) => ({ id, label, badge }))}
          />
          {tabs.map((t) => (
            <TabPanel key={t.id} idBase="record" id={t.id} active={active}>
              {t.content}
            </TabPanel>
          ))}
        </div>
      )}
    </div>
  )
}

/** A part of a record drawn as a card — `SettingsSection`'s look: an icon
 *  tile, an in-body heading, a muted line, and the part. */
export function RecordSection({
  id,
  icon,
  title,
  description,
  actions,
  children,
}: {
  id?: string
  icon?: ReactNode
  title: string
  description?: ReactNode
  /** At the heading's right — a secondary action for this part only. */
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      className="border-border min-w-0 rounded-2xl border bg-card shadow-sm"
    >
      <header className="border-border flex flex-wrap items-start gap-3 border-b px-5 py-4 sm:px-6">
        {icon && (
          <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
            {icon}
          </span>
        )}
        <div className="min-w-[12rem] flex-1">
          <h2 id={id ? `${id}-title` : undefined} className="text-foreground text-base font-semibold">
            {title}
          </h2>
          {description && <div className="text-muted-foreground mt-0.5 text-sm">{description}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      <div className="min-w-0 px-5 py-5 sm:px-6">{children}</div>
    </section>
  )
}
